import { useRef, useEffect } from 'react';
import { FOCUSABLE_SELECTORS } from './navigation.constants';

const focusableSelector = FOCUSABLE_SELECTORS.join(',');

/**
 * Get all tab-stop elements inside a container.
 * Filters out elements with tabIndex < 0 to respect roving tabindex patterns
 * (e.g., ListItemsNavigator where only the active item has tabIndex=0).
 */
function getTabStops(root) {
  return Array.from(root.querySelectorAll(focusableSelector)).filter(el => el.tabIndex >= 0);
}

/**
 * Hook that traps focus within a container.
 * Tab from the last focusable element wraps to the first.
 * Shift+Tab from the first focusable element wraps to the last.
 *
 * @returns {{ rootRef: import('react').RefObject<HTMLDivElement> }}
 */
export function useFocusTrap() {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const handleKeyDown = event => {
      if (event.key !== 'Tab') return;

      const tabStops = getTabStops(root);
      if (tabStops.length === 0) return;

      const first = tabStops[0];
      const last = tabStops[tabStops.length - 1];

      if (event.shiftKey) {
        if (document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    root.addEventListener('keydown', handleKeyDown);
    return () => root.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { rootRef };
}
